import { useState, useEffect, useRef } from 'react'
import { MessageCircle, Send, Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Sheet, SheetContent } from '@/components/ui/sheet'
import { useMarkTicketReadMutation, useUpdateTicketMutation, useTicketByIdQuery} from '../hooks/use-tickets-query'
import { tokens } from '@/locales/translations/tokens'
import { Badge } from '@/components/ui/badge'
import { queryClient } from '@/main'

const MIN_HEIGHT = 40
const MAX_HEIGHT = 96
const LINE_HEIGHT = 24

interface ChatDrawerProps {
  isOpen: boolean
  onClose: () => void
  ticket: Ticket
  onTicketUpdate: (updatedTicket: Ticket) => void
}

export function ChatDrawer({
  isOpen,
  onClose,
  ticket,
}: ChatDrawerProps) {
  const { t } = useTranslation()
  const [message, setMessage] = useState('')
  const [inputRows, setInputRows] = useState(1)
  const [localChat, setLocalChat] = useState<TicketChat[]>([])
  const markTicketReadMutation = useMarkTicketReadMutation();
  const updateTicketMutation = useUpdateTicketMutation()
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  const { data: ticketQueryData, refetch: refetchTicket } = useTicketByIdQuery(ticket.external_id);

  useEffect(() => {
    const chatData = ticketQueryData?.data?.chat || ticket.chat || [];
    setLocalChat(chatData);
  }, [ticketQueryData?.data?.chat, ticket.chat, ticket.external_id]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    if (isOpen) {
      queryClient.invalidateQueries({ queryKey: ["tickets"] });
      queryClient.refetchQueries({ queryKey: ["tickets"], type: "active" });
      refetchTicket();
    }
  }, [isOpen, refetchTicket]);

  useEffect(() => {
    scrollToBottom();
  }, [localChat]);

  useEffect(() => {
    if (
      ticket.chat &&
      ticket.chat.length > 0 
    ) {
      markTicketReadMutation.mutate({
        ticketId: ticket.external_id,
      });
    }
  }, [ticket.chat, ticket.external_id]);

  useEffect(() => {
    if (textareaRef.current) {
      if (!message) {
        setInputRows(1)
        textareaRef.current.style.height = `${MIN_HEIGHT}px`
        return
      }
      textareaRef.current.style.height = 'auto'
      const scrollHeight = textareaRef.current.scrollHeight
      let rows = Math.floor(scrollHeight / LINE_HEIGHT)
      if (rows < 1) rows = 1
      if (rows > 4) rows = 4
      setInputRows(rows)
      textareaRef.current.style.height = `${Math.min(scrollHeight, MAX_HEIGHT)}px`
    }
  }, [message])

  const handleSendMessage = async () => {
    if (message.trim() && !updateTicketMutation.isPending && ticket.status !== 'resolved') {
      const messageText = message.trim()
      
      const tempMessage: TicketChat = {
        external_id: `temp-${Date.now()}`,
        text: messageText,
        user_type: 'user',
        viewed_at: null,
        created_at: new Date().toISOString(),
      }

      setLocalChat(prev => [...prev, tempMessage])
      setMessage('')
      setInputRows(1)
      if (textareaRef.current) {
        textareaRef.current.style.height = `${MIN_HEIGHT}px`
      }

      try {
        await updateTicketMutation.mutateAsync({
          data: { chat: [{ text: messageText }] as unknown as TicketChat[] },
          externalId: ticket.external_id
        })
        
        // Aguarda um pouco e depois refetch para pegar a resposta atualizada do servidor
        setTimeout(() => {
          refetchTicket();
        }, 500);
        
      } catch (_error) {
        setLocalChat(prev => prev.filter(msg => msg.external_id !== tempMessage.external_id))
      }
    }
  }

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && ticket.status !== 'resolved') {
      e.preventDefault()
      handleSendMessage()
    }
  }

  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const isUserMessage = (msg: TicketChat) => msg.user_type === 'user'

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent side='right' className='w-[400px] p-0 sm:w-[500px] overflow-hidden'>
        <div className='flex h-full flex-col overflow-hidden'>
          <div className='flex-shrink-0 border-b p-4'>
            <div className='flex items-center gap-2 font-semibold'>
              <MessageCircle className='h-5 w-5' />
              {t(tokens.tickets.chat.title)}
            </div>
            <p className='mt-1 text-sm text-muted-foreground truncate'>
              <Badge variant="secondary">{t(tokens.tickets.chat.reference)} {ticket.title}</Badge>
            </p>
          </div>

          <div className='flex-1 overflow-y-auto overflow-x-hidden p-4'>
            <div className='space-y-4'>
              {localChat && localChat.length > 0 ? (
                localChat.map((msg) => (
                  <div
                    key={msg.external_id}
                    className={`flex ${isUserMessage(msg) ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[70%] space-y-1`}
                    >
                      <div
                        className={`rounded-2xl px-4 py-2 text-sm whitespace-pre-wrap break-words overflow-wrap-anywhere ${
                          isUserMessage(msg)
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-muted text-foreground'
                        }`}
                      >
                        {msg.text}
                      </div>
                      <div
                        className={`text-xs text-muted-foreground ${isUserMessage(msg) ? 'text-right' : 'text-left'}`}
                      >
                        {isUserMessage(msg)
                          ? `${t(tokens.tickets.chat.user_label)} • ${formatTimestamp(msg.created_at)}`
                          : `${t(tokens.tickets.chat.support_label)} • ${formatTimestamp(msg.created_at)}`}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className='py-8 text-center text-muted-foreground'>
                  <MessageCircle className='mx-auto mb-2 h-12 w-12 opacity-50' />
                  <p>{t(tokens.tickets.chat.empty_title)}</p>
                  <p className='text-sm'>
                    {t(tokens.tickets.chat.empty_subtitle)}
                  </p>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          </div>

          <div className='flex-shrink-0 border-t p-4 overflow-hidden'>
            <div className='relative flex items-center gap-2'>
              <textarea
                ref={textareaRef}
                className='flex-1 resize-none rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50'
                rows={inputRows}
                placeholder={
                  ticket.status === 'resolved' ? 
                  '✅ The ticket has been resolved.' :
                  updateTicketMutation.isPending ? 'Sending...' : 
                  t(tokens.tickets.chat.input_placeholder)}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onKeyDown={handleInputKeyDown}
                disabled={updateTicketMutation.isPending || ticket.status === 'resolved'}
                style={{
                  minHeight: `${MIN_HEIGHT}px`,
                  maxHeight: `${MAX_HEIGHT}px`,
                  overflow: inputRows === 4 ? 'auto' : 'hidden',
                }}
              />
              <Button
                size='sm'
                onClick={handleSendMessage}
                disabled={!message.trim() || updateTicketMutation.isPending || ticket.status === 'resolved'}
                className='flex-shrink-0 mt-[2px]'
              >
                {updateTicketMutation.isPending ? (
                  <Loader2 className='h-4 w-4 animate-spin' />
                ) : (
                  <Send className='h-4 w-4' />
                )}
              </Button>
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  )
}
